import { Loadable } from '@/components/loaders';
import { CustomConnectorDestinationItem } from '@/features/destinations/components/customConnectors';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { Select } from '@/lib/Selects';
import { BaseAudience } from '@/models/audiences';
import { ConnectorFlowDTO, CustomConnectorDto } from '@/models/connectors';
import { ISelectItem } from '@/models/select';
import { useCompanyValues } from '@/providers';
import { useGetConnectorFlowsQuery, useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CustomConnectorSelectionStepProps = {
  audience: BaseAudience | undefined;
  onNext: (connector: CustomConnectorDto, flow: ConnectorFlowDTO) => void;
  onCancel: () => void;
};

export const CustomConnectorSelectionStep: FC<CustomConnectorSelectionStepProps> = ({ audience, onNext, onCancel }) => {
  if (!audience) {
    return (
      <div className='flex grow flex-col items-center justify-center p-6'>
        <p className='text-gray-500'>No audience selected</p>
      </div>
    );
  }
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage: lang } = useCompanyValues();
  const { t } = useTranslation();
  const { data: connectors, isLoading: connectorsLoading } = useGetWorkspaceConnectorsQuery({ workspaceId, lang });
  const { data: flows, isLoading: flowsLoading } = useGetConnectorFlowsQuery({ workspaceId, lang });

  const isLoading = connectorsLoading || flowsLoading;

  const [selectedConnector, setSelectedConnector] = useState<CustomConnectorDto | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<ConnectorFlowDTO | null>(null);

  const availableConnectors = useMemo(() => {
    return connectors?.filter((connector) => connector.isAuthenticated) || [];
  }, [connectors]);

  const availableFlows = useMemo(() => {
    if (!selectedConnector) return [];
    return flows?.filter((flow) => flow.connectorId === selectedConnector.id && flow.active) || [];
  }, [flows, selectedConnector]);

  const flowSelectItems: ISelectItem[] = useMemo(() => {
    return availableFlows.map((flow) => ({
      id: flow.id,
      label: flow.name,
      description: flow.description,
    }));
  }, [availableFlows]);

  const handleNext = () => {
    if (!selectedConnector || !selectedFlow) return;
    onNext(selectedConnector, selectedFlow);
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON:cancel',
    onClick: onCancel,
  };

  const nextButton: StepPageActionsButton = {
    text: 'ALL:next',
    onClick: handleNext,
    disabled: !selectedConnector || !selectedFlow,
  };

  return (
    <div className='flex grow flex-col gap-6 p-6'>
      <div className='mb-6'>
        <h2 className='mb-2 text-xl font-semibold'>{t('ALL:destinations.steps.selectCustomConnector.title')}</h2>
        <p className='text-gray-600'>{t('ALL:destinations.steps.selectCustomConnector.description', { audienceName: audience?.name || 'Unknown' })}</p>
      </div>

      <Loadable isLoading={isLoading}>
        <div className='space-y-6'>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-700'>{t('ALL:destinations.steps.selectCustomConnector.availableConnectors')}</label>
            <div className='flex flex-wrap gap-4'>
              {availableConnectors.map((connector) => (
                <div
                  key={connector.id}
                  className={`cursor-pointer rounded-xl transition-all ${selectedConnector?.id === connector.id ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setSelectedConnector(connector)}
                >
                  <CustomConnectorDestinationItem connector={connector} />
                </div>
              ))}
            </div>
          </div>

          {selectedConnector && (
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-700'>{t('ALL:destinations.steps.selectCustomConnector.availableFlows')}</label>
              <Select
                items={flowSelectItems}
                value={flowSelectItems.find((item) => item.id === selectedFlow?.id)}
                onChange={(value) => {
                  const flow = flows?.find((f) => f.id === value.id);
                  setSelectedFlow(flow || null);
                }}
                placeholder={t('ALL:destinations.steps.selectCustomConnector.selectFlowPlaceholder')}
              />
            </div>
          )}
        </div>
      </Loadable>

      <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
    </div>
  );
};
